import React from "react";
import { Table as TableIcon, Plus, X } from "lucide-react";

interface TableCreationModalProps {
  isOpen: boolean;
  tableRows: number;
  tableCols: number;
  tableWithHeader: boolean;
  onRowsChange: (value: number) => void;
  onColsChange: (value: number) => void;
  onWithHeaderChange: (value: boolean) => void;
  onInsert: () => void;
  onClose: () => void;
}

export const TableCreationModal: React.FC<TableCreationModalProps> = ({
  isOpen,
  tableRows,
  tableCols,
  tableWithHeader,
  onRowsChange,
  onColsChange,
  onWithHeaderChange,
  onInsert,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <TableIcon className="w-5 h-5" />
            Insert Table
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Rows and Columns */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rows</label>
              <input
                type="number"
                min="1"
                value={tableRows}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 1) {
                    onRowsChange(val);
                  }
                }}
                onBlur={(e) => {
                  const val = parseInt(e.target.value);
                  if (isNaN(val) || val < 1) {
                    onRowsChange(1);
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Columns</label>
              <input
                type="number"
                min="1"
                value={tableCols}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 1) {
                    onColsChange(val);
                  }
                }}
                onBlur={(e) => {
                  const val = parseInt(e.target.value);
                  if (isNaN(val) || val < 1) {
                    onColsChange(1);
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
              />
            </div>
          </div>

          {/* Header Row Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="tableHeader"
              checked={tableWithHeader}
              onChange={(e) => onWithHeaderChange(e.target.checked)}
              className="w-4 h-4 text-primary-main border-gray-300 rounded focus:ring-primary-main"
            />
            <label htmlFor="tableHeader" className="text-sm font-medium text-gray-700">
              Include Header Row
            </label>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onInsert}
            className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Insert Table
          </button>
        </div>
      </div>
    </div>
  );
};


